import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  removeNotification,
  addProcessingStartedNotification,
  addSummaryReadyNotification
} from '../store/slices/notificationSlice';
import { fetchSummaries } from '../store/slices/summariesSlice';
import { fetchProcesses } from '../store/slices/processesSlice';
import NotificationSystem from './NotificationSystem';

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { notifications } = useAppSelector(state => state.notifications);
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const lastSuccessNotificationTime = useRef<number>(0);

  // Check if current page should show notifications
  const shouldShowNotifications = () => {
    const excludedPaths = ['/', '/login', '/register', '/sso-callback'];
    return !excludedPaths.includes(location.pathname) && isAuthenticated;
  };

  // Filter notifications for current user
  const userNotifications = notifications.filter(
    notification => !notification.userId || notification.userId === user?.id
  );

  // WebSocket connection for real-time notifications
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    try {
      // Use the backend URL but replace http with ws
      const wsUrl = process.env.REACT_APP_BACKEND_API_URL?.replace('http', 'ws') || 'ws://localhost:8000';
      const token = localStorage.getItem('aihub_token');
      const ws = new WebSocket(`${wsUrl}/ws/notifications/${user.id}?token=${token}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected for notifications');
        reconnectAttempts.current = 0;
        wsRef.current = ws;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received WebSocket notification:', data);
          
          // Handle different types of notifications
          switch (data.type) {
            case 'summary_processing_started':
              const now = Date.now();
              const timeSinceLastSuccess = now - lastSuccessNotificationTime.current;

              // If less than 2.5 seconds since last success notification, delay this one
              if (timeSinceLastSuccess < 2500) {
                const delay = 2500 - timeSinceLastSuccess;
                setTimeout(() => {
                  dispatch(addProcessingStartedNotification({
                    userId: user.id,
                    summaryId: data.summary_id,
                    processId: data.summary_id
                  }));
                }, delay);
              } else {
                dispatch(addProcessingStartedNotification({
                  userId: user.id,
                  summaryId: data.summary_id,
                  processId: data.summary_id
                }));
              }
              // Refresh data so the new process appears in the list
              dispatch(fetchProcesses());
              break;
              
            case 'summary_ready':
              lastSuccessNotificationTime.current = Date.now();
              dispatch(addSummaryReadyNotification({
                userId: user.id,
                summaryId: data.summary_id,
                title: data.title,
                processId: data.summary_id
              }));

              // Auto-refresh so the completed summary appears on the page
              dispatch(fetchSummaries());
              dispatch(fetchProcesses());

              // Update global reference
              if (typeof window !== 'undefined' && window.notificationProvider) {
                window.notificationProvider.lastSuccessNotificationTime = lastSuccessNotificationTime.current;
              }
              break;
              
            default:
              console.log('Unknown notification type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        wsRef.current = null;
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Attempting to reconnect in ${delay}ms...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [dispatch, isAuthenticated, user?.id]);

  // Persist notifications across page changes
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Save notifications to sessionStorage for persistence across page changes
      const notificationsKey = `notifications_${user.id}`;
      sessionStorage.setItem(notificationsKey, JSON.stringify(userNotifications));
    }
  }, [userNotifications, isAuthenticated, user?.id]);

  // Connect WebSocket when user is authenticated
  useEffect(() => {
    // Expose lastSuccessNotificationTime to global scope for delay logic
    if (typeof window !== 'undefined') {
      window.notificationProvider = {
        lastSuccessNotificationTime: lastSuccessNotificationTime.current
      };
    }
    
    if (isAuthenticated && user?.id) {
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket, isAuthenticated, user?.id]);

  // Load persisted notifications on mount
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const notificationsKey = `notifications_${user.id}`;
      const persistedNotifications = sessionStorage.getItem(notificationsKey);
      
      if (persistedNotifications) {
        try {
          const parsed = JSON.parse(persistedNotifications);
          // Only restore notifications that are less than 1 hour old
          const oneHourAgo = Date.now() - (60 * 60 * 1000);
          const hasAnyValid = Array.isArray(parsed) && parsed.some((notif: any) =>
            notif?.createdAt && notif.createdAt > oneHourAgo
          );
          
          // Restore valid notifications to Redux store if needed
          // This would require additional actions in the slice
          if (!hasAnyValid) {
            sessionStorage.removeItem(notificationsKey);
          }
        } catch (error) {
          console.error('Error parsing persisted notifications:', error);
          sessionStorage.removeItem(notificationsKey);
        }
      }
    }
  }, [isAuthenticated, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const handleCloseNotification = (id: string) => {
    dispatch(removeNotification(id));
  };

  return (
    <>
      {children}
      {shouldShowNotifications() && userNotifications.length > 0 && (
        <NotificationSystem
          notifications={userNotifications}
          onClose={handleCloseNotification}
          position={{ vertical: 'bottom', horizontal: 'right' }}
        />
      )}
    </>
  );
};

export default NotificationProvider;